import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SetDisplaySidenav } from '@app/app/app-frame/store/app-frame.actions';
import { AppFrameState } from '@app/app/app-frame/store/app-frame.state';
import { LucideChevronLeft, LucideChevronRight, LucideIconInput } from '@lucide/angular';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, filter, takeUntil } from 'rxjs';
import { AdminSidebarSectionComponent } from '../admin-sidebar-section/admin-sidebar-section.component';
import { SidebarBackLink } from '../secondary-sidebar/secondary-sidebar.component';

const MOBILE_MAX_WIDTH = 900;

@Component({
  selector: 'ptl-admin-sidebar',
  templateUrl: './admin-sidebar.component.html',
  styleUrls: ['./admin-sidebar.component.scss'],
})
export class AdminSidebarComponent implements OnInit, OnDestroy {
  @Input() type = '';
  @Input() title = '';
  @Input() icon: LucideIconInput;
  @Input() isCollapsed = false;
  @Input() rightSidebar: boolean;
  @Input() backLink: SidebarBackLink | null = null;

  @Select(AppFrameState.isMobileMenuExpanded)
  mobileMenuExpanded$: Observable<boolean>;

  protected readonly LucideChevronLeft = LucideChevronLeft;
  protected readonly LucideChevronRight = LucideChevronRight;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new SetDisplaySidenav(false));

    this.isCollapsed = this.isMobileViewport();

    this.mobileMenuExpanded$
      .pipe(
        filter(() => this.isMobileViewport()),
        takeUntil(this.destroy$),
      )
      .subscribe((expanded) => {
        this.isCollapsed = !expanded;
      });

    // On mobile, collapse the sidebar once the user navigates via a menu item so the content is visible.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.isMobileViewport()) {
          this.isCollapsed = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new SetDisplaySidenav(true));
    // Leaving the area resets the first-load state so re-entering expands all sections.
    AdminSidebarSectionComponent.resetFirstLoad();
  }

  onToggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  expand(): void {
    this.isCollapsed = false;
  }

  private isMobileViewport(): boolean {
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  }
}
